import {
  AddressDto,
  EstablishmentDto,
} from '@establishment/models/dto/establishment-mutation.dto';
import { Divider, Stack } from '@mui/material';
import Jtab, { JTabItem } from '@atoms/JTab';
import { useNavigate, useParams } from 'react-router-dom';

import EstablishmentCredits from '../EstablishmentCredits';
import EstablishmentDetails from '@establishment/components/EstablishmentDetails';
import { JButton } from '@atoms/JButton';
import { Loading } from '@atoms/Loading';
import Paths from '@/routes/paths';
import { TopBar } from '@/layouts/TopBar';
import { useEditEstablishmenRequest } from '@establishment/mutations/establishmentMutations';
import { useEffect } from 'react';
import useEstablishmentMutation from '@establishment/hooks/useEstablishmentMutation';
import { useGetEstablishmentById } from '@establishment/queries/useGetEstablishment';
import useJSnackbar from '@/hooks/useJSnackbar';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const EstablishmentEditForm = () => {
  const { showSnackbar } = useJSnackbar();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const itemsList: JTabItem[] = [
    {
      title: 'GENERAL_INFORMATIONS',
      component: <EstablishmentDetails />,
    },
    {
      title: 'CREDITS',
      component: <EstablishmentCredits id={id} />,
    },
  ];

  const { data, isSuccess, isLoading } = useGetEstablishmentById(id, !!id);
  const { setPayload, setSectors, setEstablishment, isDirty, payload } =
    useEstablishmentMutation();
  const {
    mutate: editEstablishmentMutation,
    isLoading: isEditLoading,
    isSuccess: isEditSuccess,
    isError: isEditError,
  } = useEditEstablishmenRequest();

  const dtoData = data?.data;
  const addressDtoData = data?.data.address;

  const fillForm = async () => {
    if (!dtoData || !addressDtoData) return;

    // fill payload
    const address: AddressDto = {
      countryName: addressDtoData.country.name,
      countryId: addressDtoData.country.id,
      cityId: addressDtoData?.city?.id ?? undefined,
      cityName: addressDtoData.cityName ?? '',
      additionalStreet: addressDtoData.additionalStreet ?? undefined,
      street: addressDtoData.street,
    };
    const establishment: EstablishmentDto = {
      name: dtoData.name,
      enterpriseId: dtoData.enterprise.id,
      mobilePhone: dtoData.mobilePhone,
      phone: dtoData.phone,
      email: dtoData.email,
      siret: dtoData.siret,
      activityAreaIds: dtoData.activityAreas.map((item) => item.romeDomain.id),
      creditsAlertThreshold: dtoData.creditsAlertThreshold,
    };

    setPayload({
      addressDto: address,
      establishmentDto: establishment,
      videoUrl: dtoData.videoUrl ?? undefined,
      thumbnailUrl: dtoData.thumbnailUrl ?? undefined,
      id: dtoData.id,
    });
  };
  useEffect(() => {
    if (isSuccess) {
      fillForm();
      setEstablishment(dtoData);
    }
    setSectors(
      dtoData?.activityAreas
        .filter((item) => item !== undefined)
        .map((item) => ({
          label: item.romeDomain.name,
          value: item.romeDomain.id,
        })),
    );
  }, [isSuccess, dtoData]);

  useEffect(() => {
    if (!isEditSuccess) return;
    queryClient.invalidateQueries(['establishment', id]);
    queryClient.invalidateQueries(['establishments']);
    navigate(Paths.EstablishmentPage);
    showSnackbar(t('ENTERPRISE_EDIT_SUCCESS'), 'success');
  }, [isEditSuccess, navigate, queryClient, id, showSnackbar, t]);

  useEffect(() => {
    if (isEditError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [isEditError]);

  if (isLoading || isEditLoading) return <Loading />;

  return (
    <>
      <TopBar title={t('EDIT_ESTABLISHMENT')}>
        <Stack direction={'row'} gap={2}>
          <JButton
            onClick={() => navigate(Paths.EstablishmentPage)}
            variant="text"
          >
            {t('CANCEL')}
          </JButton>
          {isDirty && (
            <JButton
              onClick={() => {
                if (payload && id) {
                  editEstablishmentMutation({
                    id,
                    formData: payload,
                  });
                }
              }}
              size="small"
              variant="contained"
              color="secondary"
            >
              {t('SAVE')}
            </JButton>
          )}
        </Stack>
      </TopBar>
      <Divider />
      <Jtab items={itemsList} />
    </>
  );
};

export default EstablishmentEditForm;
